import type { Theme } from '@goodtime/engine';
import { withHevcCopies } from './slotAssets';
// Veo-generated transparent celebration videos. See forestTheme.ts for pipeline notes.
/**
 * Sunset Mesa — the frontier-biome themed slot. Mapping:
 * cherry→cactus, lemon→horseshoe, orange→cowboy_hat, bell→wagon_wheel,
 * star(wild)→tornado_wild, seven→sheriff_star, gem→boot,
 * scatter→sunset_scatter, money→gold_nugget.
 */
export const FRONTIER_THEME: Theme = {
  id: 'frontier',
  name: 'Sunset Mesa',
  palette: {
    accent: 0xc25a3f,         // sun-baked terracotta
    secondary: 0xf4b860,      // dust-yellow
    warm: 0xffe28a,           // golden hour
    frameFill: 0x3a1e15,      // deep wood
    bgGradientTop: 0x4a2418,
    bgGradientBottom: 0xc97a4a,
    dimOverlay: 0x10070a,
  },
  slot: {
    symbols: [
      { id: 'cherry',  label: 'Cactus',       glyph: '🌵', tint: 0x6bbf5a, weight: 5,   textureAlias: 'frontier:cherry',
        winVideoAlias: 'frontier:cherry:win:video', winVideoSpeed: 2 },
      { id: 'lemon',   label: 'Horseshoe',    glyph: '🐴', tint: 0xc8a45a, weight: 5,   textureAlias: 'frontier:lemon',
        winVideoAlias: 'frontier:lemon:win:video', winVideoSpeed: 2 },
      { id: 'orange',  label: 'Cowboy Hat',   glyph: '🤠', tint: 0xc25a3f, weight: 4,   textureAlias: 'frontier:orange',
        winVideoAlias: 'frontier:orange:win:video', winVideoSpeed: 2 },
      { id: 'bell',    label: 'Wagon Wheel',  glyph: '🛞', tint: 0x8a5a3a, weight: 3,   textureAlias: 'frontier:bell',
        winVideoAlias: 'frontier:bell:win:video', winVideoSpeed: 2 },
      { id: 'star',    label: 'Tornado',      glyph: '🌪️', tint: 0xeed09a, weight: 2,   textureAlias: 'frontier:star', // wild
        winVideoAlias: 'frontier:star:win:video', winVideoSpeed: 2 },
      { id: 'seven',   label: 'Sheriff Star', glyph: '⭐', tint: 0xffd23b, weight: 1,   textureAlias: 'frontier:seven',
        winVideoAlias: 'frontier:seven:win:video', winVideoSpeed: 2 },
      { id: 'gem',     label: 'Boot',         glyph: '👢', tint: 0x6b3a25, weight: 1,   textureAlias: 'frontier:gem',
        winVideoAlias: 'frontier:gem:win:video', winVideoSpeed: 2 },
      { id: 'scatter', label: 'Sunset',       glyph: '🌅', tint: 0xff8a3d, weight: 1,   textureAlias: 'frontier:scatter',
        winVideoAlias: 'frontier:scatter:win:video', winVideoSpeed: 2 },
      { id: 'money',   label: 'Gold Nugget',  glyph: '💰', tint: 0xffd23b, weight: 1.5,
        textureAlias: 'frontier:money',
        winVideoAlias: 'frontier:money:win:video', winVideoSpeed: 2,
        isMoney: true },
      // Gem-on-reel — producer brief 2026-06-04. Scatter-style bonus
      // shared across every biome (registered via SHARED_REEL_SYMBOLS
      // in themes/index.ts). Weights deliberately rare so a gem
      // landing feels like a treat rather than background noise.
      { id: 'gem_t5',  label: 'Gems',     glyph: '💎', tint: 0x3ea7e8, weight: 0.10, gemValue: 5,  textureAlias: 'shared:reel:gem_5' },
      { id: 'gem_t10', label: 'Gem Pile', glyph: '💎', tint: 0x3ea7e8, weight: 0.05, gemValue: 10, textureAlias: 'shared:reel:gem_10' },
    ],
    wildSymbolId: 'star',
    scatterSymbolId: 'scatter',
  },
  wheel: {
    segments: [
      { id: 'x1-a',   kind: 'multiplier', label: '×1',  tint: 0xc25a3f, multiplier: 1,  weight: 4 },
      { id: 'x2-a',   kind: 'multiplier', label: '×2',  tint: 0xf4b860, multiplier: 2,  weight: 3 },
      { id: 'x0-a',   kind: 'multiplier', label: '✕',   tint: 0x3a1e15, multiplier: 0,  weight: 2 },
      { id: 'x3-a',   kind: 'multiplier', label: '×3',  tint: 0xc97a4a, multiplier: 3,  weight: 2 },
      { id: 'x1-b',   kind: 'multiplier', label: '×1',  tint: 0xc25a3f, multiplier: 1,  weight: 3 },
      { id: 'x5-a',   kind: 'multiplier', label: '×5',  tint: 0xffe28a, multiplier: 5,  weight: 1 },
      { id: 'x0-b',   kind: 'multiplier', label: '✕',   tint: 0x3a1e15, multiplier: 0,  weight: 2 },
      { id: 'x10-a',  kind: 'multiplier', label: '×10', tint: 0xffe28a, multiplier: 10, weight: 1 },
      { id: 'x2-b',   kind: 'multiplier', label: '×2',  tint: 0xf4b860, multiplier: 2,  weight: 3 },
      { id: 'x1-c',   kind: 'multiplier', label: '×1',  tint: 0xc25a3f, multiplier: 1,  weight: 3 },
      { id: 'x3-b',   kind: 'multiplier', label: '×3',  tint: 0xc97a4a, multiplier: 3,  weight: 2 },
      { id: 'jackpot',kind: 'multiplier', label: '×25', tint: 0xffd23b, multiplier: 25, weight: 1 },
    ],
  },
  assets: {
    slot: {
      cherry:  { alias: 'frontier:cherry',  path: 'reel/frontier/cactus.png' },
      lemon:   { alias: 'frontier:lemon',   path: 'reel/frontier/horseshoe.png' },
      orange:  { alias: 'frontier:orange',  path: 'reel/frontier/cowboy_hat.png' },
      bell:    { alias: 'frontier:bell',    path: 'reel/frontier/wagon_wheel.png' },
      star:    { alias: 'frontier:star',    path: 'reel/frontier/tornado_wild.png' },
      seven:   { alias: 'frontier:seven',   path: 'reel/frontier/sheriff_star.png' },
      gem:     { alias: 'frontier:gem',     path: 'reel/frontier/boot.png' },
      scatter: { alias: 'frontier:scatter', path: 'reel/frontier/sunset_scatter.png' },
      money:   { alias: 'frontier:money',   path: 'reel/frontier/gold_nugget.png' },
    },
    slotWin: withHevcCopies({
      cherry:  { alias: 'frontier:cherry:win:video',  path: 'reel/frontier/cactus_win.webm' },
      lemon:   { alias: 'frontier:lemon:win:video',   path: 'reel/frontier/horseshoe_win.webm' },
      orange:  { alias: 'frontier:orange:win:video',  path: 'reel/frontier/cowboy_hat_win.webm' },
      bell:    { alias: 'frontier:bell:win:video',    path: 'reel/frontier/wagon_wheel_win.webm' },
      star:    { alias: 'frontier:star:win:video',    path: 'reel/frontier/tornado_wild_win.webm' },
      seven:   { alias: 'frontier:seven:win:video',   path: 'reel/frontier/sheriff_star_win.webm' },
      gem:     { alias: 'frontier:gem:win:video',     path: 'reel/frontier/boot_win.webm' },
      scatter: { alias: 'frontier:scatter:win:video', path: 'reel/frontier/sunset_scatter_win.webm' },
      money:   { alias: 'frontier:money:win:video',   path: 'reel/frontier/gold_nugget_win.webm' },
    }),
    scene: {
      background:       { alias: 'frontier:bg',         path: 'environment/frontier.png' },
      columnBackground: { alias: 'frontier:column_bg',  path: 'reel/frontier_column_bg.png' },
      border:           { alias: 'frontier:border',     path: 'brand/border_frontier.png' },
    },
  },
};
